import _ from "lodash";
import { action, autorun, observable, makeObservable } from "mobx";
import config, { afterExtendConfig } from "./config";
import { v4 } from "uuid";
import { fetchLoginStatus } from "./serviceCalls";
import swal from "sweetalert2";
import { name } from "../package.json";
import UserStorage from "shared/UserStorage";

class AuthStore {
  authToken = sessionStorage.authToken;
  username = sessionStorage.username;
  prefsPromise;
  id = v4();
  requiredVendorLogins = [];
  vendorLoginRequired = false;

  get interactionEvents() {
    return ["mousemove", "scroll", "keydown", "focus", "wheel", "mousedown"];
  }

  constructor() {
    makeObservable(this, {
      authToken: observable,
      username: observable,
      prefsPromise: observable,
      requiredVendorLogins: observable,
      vendorLoginRequired: observable,
      deauth: action,
      concludeVendorCreds: action,
    });
    this.deauth = this.deauth.bind(this);
    afterExtendConfig(this.startTimer);
    afterExtendConfig(this.checkVendorCreds);
    window.addEventListener("message", this.handlePostedMessage, false);

    if ("BroadcastChannel" in self) {
      this.broadcastChannel = new BroadcastChannel(name);
      this.broadcastChannel.addEventListener(
        "message",
        this.onReceiveBroadcast,
      );
      this.broadcastChannel.addEventListener("messageerror", (err) => {
        console.error("auth message error", err);
      });
      this.broadcastChannel.postMessage({ initialized: { from: this.id } });
    }

    if (sessionStorage.logoutCode === "INACTIVE") {
      sessionStorage.removeItem("logoutCode");
      this.alertSessionEnded();
    }

    autorun(() => {
      if (this.authToken) {
        sessionStorage.authToken = this.authToken;
        sessionStorage.username = this.username;
        if (this.username && this.authToken && !this.storage) {
          if (config.siteId) {
            this.initStorage();
          } else {
            afterExtendConfig(() => this.initStorage());
          }
        }
        if (config.timeout && !this.timerId) {
          this.startTimer();
          this.interactionEvents.forEach((eventName) =>
            window.addEventListener(
              eventName,
              _.throttle(this.onInteraction, 100),
            ),
          );
        }
      }
    });
  }

  handlePostedMessage = (event) => {
    const hasSameOrigin = event.origin === location.origin;
    const isDevEnvironment = process.env.NODE_ENV === "development";
    const { loggedIn, showPatientHeader } = event.data || {};

    if (loggedIn && (hasSameOrigin || isDevEnvironment)) {
      this.username = loggedIn.username;
      this.authToken = loggedIn.authToken;
    }
    if (typeof showPatientHeader !== "undefined") {
      this.appStore.showPatientHeader = showPatientHeader;
    }

    // treat this as an interaction
    this.onInteraction();
  };

  timeoutSession = () => {
    if (config.pm.preventTimeout) {
      this.broadcastChannel.postMessage({ requestTimeoutBlocks: true });
      this.timerId = setTimeout(() => {
        this.deauth("INACTIVE");
      }, 1000);
    } else {
      this.deauth("INACTIVE");
    }
  };

  login({ authToken, username }) {
    this.authToken = authToken;
    this.username = username;
    sessionStorage.authToken = authToken;
    sessionStorage.username = username;
    this.broadcastChannel?.postMessage({ login: { username } });
    this.checkVendorCreds();
    this.initStorage();
  }

  initStorage() {
    if (this.username)
      this.storage = new UserStorage({
        useLocalStorage: true,
        authStore: this,
      });
  }

  checkVendorCreds = async () => {
    if (!this.authToken) return;
    const { moduleIdStatuses } = await fetchLoginStatus();
    this.requiredVendorLogins = moduleIdStatuses;
    this.vendorLoginRequired = !!moduleIdStatuses?.length;
  };

  concludeVendorCreds = () => {
    this.requiredVendorLogins = [];
    this.vendorLoginRequired = false;
  };

  deauth(code) {
    this.loggedOut = true;
    this.authToken = undefined;
    sessionStorage.clear();
    this.storage.save();
    if (code === "INACTIVE") {
      sessionStorage.logoutCode = code;
    }
    if (code !== "BROADCAST") {
      this.broadcastChannel?.postMessage({ logout: { code } });
    }
    location.hash = "";
    if (config.ENABLE_SITE_SWITCHER && config.SITE_LAUNCHER_URL) {
      window.location = config.SITE_LAUNCHER_URL;
    } else {
      location.reload();
    }
    if (this.timerId) {
      this.timerId = undefined;
      this.interactionEvents.forEach((eventName) =>
        window.removeEventListener(eventName, this.startTimer),
      );
    }
  }

  startTimer = () => {
    if (!this.authToken) return;
    if (this.componentsBlockingTimeout.size) return;
    window.clearTimeout(this.timerId);
    if (config.timeout) {
      this.timerId = window.setTimeout(
        () => this.timeoutSession(),
        config.timeout,
      );
    }
  };

  componentsBlockingTimeout = new Set();
  blockTimeout(component) {
    window.clearTimeout(this.timerId);
    this.componentsBlockingTimeout.add(component);
    return () => this.unblockTimeout(component);
  }

  unblockTimeout(component) {
    this.componentsBlockingTimeout.delete(component);
    if (this.componentsBlockingTimeout.size === 0) {
      this.startTimer();
    }
  }

  onInteraction = () => {
    this.startTimer();
    this.broadcastChannel?.postMessage({ interaction: true });
  };

  onReceiveBroadcast = (e) => {
    const {
      login,
      logout,
      interaction,
      initialized,
      loggedIn,
      requestTimeoutBlocks,
      blockTimeout,
    } = e.data;
    if (this.authToken) {
      if (logout) {
        if (
          !(logout.code === "INACTIVE" && this.componentsBlockingTimeout.size)
        ) {
          this.deauth("BROADCAST");
        }
      }
      if (login && login.username !== this.username) {
        this.deauth("BROADCAST");
      }
      if (interaction) {
        this.startTimer();
      }
      if (initialized) {
        const { username, authToken } = this;
        this.broadcastChannel?.postMessage({
          loggedIn: { username, authToken, to: initialized.from },
        });
      }
      if (requestTimeoutBlocks) {
        if (this.componentsBlockingTimeout.size)
          this.broadcastChannel.postMessage({ blockTimeout: true });
      }
      if (blockTimeout) {
        if (this.timerId) {
          clearTimeout(this.timerId);
          this.startTimer();
        }
      }
    } else if (loggedIn && !location.hash.match(/\/sharelink/)) {
      if (this.id === loggedIn.to) {
        this.authToken = loggedIn.authToken;
        this.username = loggedIn.username;
      }
    }
  };

  alertSessionEnded() {
    try {
      swal.fire({
        title: "Session Ended",
        text: "You have been logged out due to inactivity.",
        type: "error",
      });
    } catch (error) {
      console.log('Error showing "session ended" message');
    }
  }
}

const authStore = new AuthStore();

export default authStore;
